import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SecondaryButton from '../atoms/SecondaryButton'
import TablePagination from './TablePagination'
import DocumentListRow from './DocumentListRow'
import DocumentFileSvg from '../other/svg/DocumentFileSvg'
import PlusSvg from '../other/svg/PlusSvg'
import { Document } from 'src/redux/documentsReducer'
import { VehicleDocumentType, documentTypeLabelKey, formatDocumentDate } from 'src/helpers/addVehicleDocument'

interface VehicleDocumentsPanelProps {
  documents: Document[]
  currentPage: number
  totalPages: number
  onSelectDocumentType: (documentType: VehicleDocumentType) => void
  onOpenDocument: (document: Document) => void
  onDownloadDocument: (document: Document) => void
  onEditDocument: (document: Document) => void
  onDeleteDocument: (document: Document) => void
  onPageChange: (page: number) => void
}

const menuShadow =
  '0px 8px 16px 0px rgba(0,0,0,0.08), 0px 4px 8px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'

const documentTypeOptions: Array<{
  type: VehicleDocumentType
  labelKey: 'my_vehicles.document_invoice' | 'my_vehicles.document_sell_contract' | 'my_vehicles.document_import'
}> = [
  { type: 'invoice', labelKey: 'my_vehicles.document_invoice' },
  { type: 'sell_contract', labelKey: 'my_vehicles.document_sell_contract' },
  { type: 'import_document', labelKey: 'my_vehicles.document_import' }
]

const VehicleDocumentsPanel = ({
  documents,
  currentPage,
  totalPages,
  onSelectDocumentType,
  onOpenDocument,
  onDownloadDocument,
  onEditDocument,
  onDeleteDocument,
  onPageChange
}: VehicleDocumentsPanelProps): JSX.Element => {
  const { t } = useTranslation()
  const isEmpty = documents.length === 0
  const [menuAnchor, setMenuAnchor] = useState<'header' | 'empty' | null>(null)
  const [openRowKey, setOpenRowKey] = useState<string | null>(null)
  const headerMenuRef = useRef<HTMLDivElement>(null)
  const emptyMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClick = (event: MouseEvent): void => {
      const headerContains = headerMenuRef.current?.contains(event.target as Node) === true
      const emptyContains = emptyMenuRef.current?.contains(event.target as Node) === true
      if (!headerContains && !emptyContains) {
        setMenuAnchor(null)
      }
    }

    if (menuAnchor !== null) {
      document.addEventListener('mousedown', handleClick)
    }

    return () => {
      document.removeEventListener('mousedown', handleClick)
    }
  }, [menuAnchor])

  const handleSelectType = (documentType: VehicleDocumentType): void => {
    setMenuAnchor(null)
    onSelectDocumentType(documentType)
  }

  const toggleMenu = (anchor: 'header' | 'empty'): void => {
    setMenuAnchor(menuAnchor === anchor ? null : anchor)
  }

  const documentLabel = (documentType: string): string => {
    const typeLabelKey = documentTypeLabelKey(documentType)
    if (typeLabelKey === null) {
      return documentType
    }
    return t(typeLabelKey)
  }

  return (
    <div className="flex-1 self-stretch max-w-[474px] bg-neutral-50 rounded-lg outline outline-1 outline-offset-[-1px] outline-borders-border-base inline-flex flex-col justify-start items-start">
      <div className="self-stretch h-14 px-4 py-3 inline-flex justify-between items-center">
        <div className="justify-start text-foregrounds-fg-base text-sm font-semibold font-Montserrat leading-5">
          {t('my_vehicles.documents')}
        </div>
        <div className="relative" ref={headerMenuRef}>
          <SecondaryButton
            size="base"
            focused={menuAnchor === 'header'}
            onClick={() => {
              toggleMenu('header')
            }}
            icon={<PlusSvg className="w-3.5 h-3.5 text-icon-secondary" />}>
            {t('my_vehicles.add_document')}
          </SecondaryButton>
          {menuAnchor === 'header' && (
            <div
              className="w-44 p-1 absolute right-0 top-full z-20 bg-backgrounds-bg-field-component rounded-lg inline-flex flex-col justify-start items-start overflow-hidden"
              style={{ boxShadow: menuShadow }}>
              <div className="self-stretch flex flex-col justify-start items-start">
                {documentTypeOptions.map((option) => (
                  <button
                    type="button"
                    key={option.type}
                    onClick={() => {
                      handleSelectType(option.type)
                    }}
                    className="self-stretch px-2 py-1 bg-backgrounds-bg-field-component rounded-sm inline-flex justify-start items-center hover:bg-backgrounds-bg-field-component-hover">
                    <div className="flex-1 text-left justify-start text-foregrounds-fg-base text-xs font-medium font-Montserrat leading-5">
                      {t(option.labelKey)}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="self-stretch flex-1 px-3 pb-3 flex flex-col justify-start items-start gap-2.5 overflow-visible">
        {isEmpty ? (
          <div className="self-stretch flex-1 p-6 bg-backgrounds-bg-field-component rounded-lg outline outline-1 outline-offset-[-1px] outline-borders-border-base flex flex-col justify-center items-center gap-6 overflow-visible">
            <div className="self-stretch flex flex-col justify-center items-center gap-3">
              <div className="size-8 flex justify-center items-center">
                <DocumentFileSvg className="size-8 text-foregrounds-fg-base" />
              </div>
              <div className="self-stretch flex flex-col justify-center items-center gap-1">
                <div className="self-stretch text-center justify-start text-foregrounds-fg-base text-xl font-semibold font-Montserrat leading-5">
                  {t('my_vehicles.documents_empty_title')}
                </div>
                <div className="self-stretch text-center justify-start text-foregrounds-fg-subtle text-sm font-medium font-Montserrat leading-5">
                  {t('my_vehicles.documents_empty_subtitle')}
                </div>
              </div>
            </div>
            <div className="relative pb-2 inline-flex justify-center items-center" ref={emptyMenuRef}>
              <SecondaryButton
                focused={menuAnchor === 'empty'}
                onClick={() => {
                  toggleMenu('empty')
                }}
                icon={<PlusSvg className="w-3.5 h-3.5 text-icon-secondary" />}>
                {t('my_vehicles.add_document')}
              </SecondaryButton>
              {menuAnchor === 'empty' && (
                <div
                  className="w-48 p-1 absolute left-1/2 top-full z-20 -translate-x-1/2 bg-backgrounds-bg-field-component rounded-lg inline-flex flex-col justify-start items-start overflow-hidden"
                  style={{ boxShadow: menuShadow }}>
                  <div className="self-stretch flex flex-col justify-start items-start">
                    {documentTypeOptions.map((option) => (
                      <button
                        type="button"
                        key={option.type}
                        onClick={() => {
                          handleSelectType(option.type)
                        }}
                        className="self-stretch px-2 py-1 bg-backgrounds-bg-field-component rounded-sm inline-flex justify-start items-center hover:bg-backgrounds-bg-field-component-hover">
                        <div className="flex-1 text-left justify-start text-foregrounds-fg-base text-xs font-medium font-Montserrat leading-5">
                          {t(option.labelKey)}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="self-stretch flex-1 relative bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-borders-border-base flex flex-col justify-start items-center overflow-visible">
            <div className="self-stretch flex-1 flex flex-col justify-start items-start overflow-visible">
              {documents.map((document, index) => {
                const rowKey = `${document.document_type}-${document.id}`
                return (
                  <React.Fragment key={rowKey}>
                    {index > 0 && <div className="self-stretch h-px bg-borders-border-base" />}
                    <DocumentListRow
                      label={documentLabel(document.document_type)}
                      date={formatDocumentDate(document.created_at)}
                      isMenuOpen={openRowKey === rowKey}
                      onToggleMenu={() => {
                        setOpenRowKey(openRowKey === rowKey ? null : rowKey)
                      }}
                      onCloseMenu={() => {
                        setOpenRowKey(null)
                      }}
                      onOpen={() => {
                        onOpenDocument(document)
                      }}
                      onDownload={() => {
                        onDownloadDocument(document)
                      }}
                      onEdit={() => {
                        onEditDocument(document)
                      }}
                      onDelete={() => {
                        onDeleteDocument(document)
                      }}
                    />
                  </React.Fragment>
                )
              })}
            </div>
            <TablePagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
          </div>
        )}
      </div>
    </div>
  )
}

export default VehicleDocumentsPanel
